import { useState, useEffect, useContext } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { firestore } from '../firebase';
import { fetchGame } from '../util/api/game';

/**
 * Custom hook for managing Firebase notifications
 * Extracted from interaction.js for reuse in mobile and web components
 */
export const useNotifications = (authenticatedUserHandle) => {
  const [newFollowers, setNewFollowers] = useState([]);
  const [newComments, setNewComments] = useState([]);
  const [notifications, setNotifications] = useState({
    comments: {},
    likes: {},
    follows: { followers: [] },
    likesByGameId: {}
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch all notification types
  const fetchNotifications = async () => {
    if (!authenticatedUserHandle) return;

    try {
      setLoading(true);
      setError(null);

      // Fetch follower notifications
      const followersQuery = query(
        collection(firestore, "notifications"), 
        where("followedTo", "==", authenticatedUserHandle)
      );
      
      const followersData = [];
      const followersSnapshot = await getDocs(followersQuery);
      followersSnapshot.forEach((doc) => {
        const followerData = doc.data();
        if (!followerData.status) {
          followerData.status = 'unread';
        }
        followersData.push({
          id: doc.id,
          ...followerData
        });
      });
      
      // Sort followers by creation time (newest first)
      followersData.sort((a, b) => {
        const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
        const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
        return timeB - timeA;
      });
      
      setNewFollowers(followersData);
      
      // Fetch comment notifications
      const commentsQuery = query(
        collection(firestore, "notifications"), 
        where("commentTo", "==", authenticatedUserHandle),
        where("type", "==", "new_comment")
      );
      
      const commentsData = [];
      const commentsSnapshot = await getDocs(commentsQuery);
      commentsSnapshot.forEach((doc) => {
        const commentData = doc.data();
        if (!commentData.status) {
          commentData.status = 'unread';
        }
        commentsData.push({
          id: doc.id,
          ...commentData
        });
      });
      
      // Sort comments by creation time (newest first)
      commentsData.sort((a, b) => {
        const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
        const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
        return timeB - timeA;
      });
      
      setNewComments(commentsData);
      
      // Fetch like notifications
      const likesQuery = query(
        collection(firestore, "notifications"), 
        where("likedTo", "==", authenticatedUserHandle),
        where("type", "==", "new_like")
      );
      
      const likesData = [];
      const likesSnapshot = await getDocs(likesQuery);
      likesSnapshot.forEach((doc) => {
        const likeData = doc.data();
        if (!likeData.status) {
          likeData.status = 'unread';
        }
        likesData.push({
          id: doc.id,
          ...likeData
        });
      });
      
      // Sort likes by creation time (newest first)
      likesData.sort((a, b) => {
        const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
        const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
        return timeB - timeA;
      });
      
      // Group likes by gameId
      const likesByGameId = likesData.reduce((groups, like) => {
        const gameId = like.gameId;
        if (!groups[gameId]) {
          groups[gameId] = [];
        }
        groups[gameId].push(like);
        return groups;
      }, {});
      
      // Add the likes data to comments state grouped by gameId
      const updatedComments = [...commentsData];
      
      // If we have likes but no comments for a game, create a placeholder entry
      Object.keys(likesByGameId).forEach(gameId => {
        const gameExists = commentsData.some(comment => comment.gameId === gameId);
        if (!gameExists) {
          updatedComments.push({
            id: `placeholder-${gameId}`,
            gameId: gameId,
            isPlaceholder: true,
            status: 'read'
          });
        }
      });
      
      setNewComments(updatedComments);
      
      // Store the likes by gameId for rendering
      setNotifications(prev => ({
        ...prev,
        likesByGameId: likesByGameId
      }));
      
    } catch (err) {
      console.error("Error fetching notifications:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Check if a game exists by fetching it
  const checkGameExists = async (gameId) => {
    try {
      const gameData = await fetchGame(gameId);
      if (gameData && gameData.error && gameData.isNotFound) {
        return false;
      } else if (gameData && !gameData.error) {
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error checking game:', error);
      return false;
    }
  };

  // Helper functions
  const hasNewFollowers = () => {
    return newFollowers.some(follower => follower.status === 'unread');
  };

  const hasNewComments = () => {
    return newComments.some(comment => comment.status === 'unread');
  };

  const hasNewLikes = () => {
    if (!notifications.likesByGameId) return false;
    return Object.values(notifications.likesByGameId)
      .flat()
      .some(like => like.status === 'unread');
  };

  const getNotificationCounts = () => {
    return {
      followers: newFollowers.filter(f => f.status === 'unread').length,
      comments: newComments.filter(c => c.status === 'unread').length,
      likes: notifications.likesByGameId ? 
        Object.values(notifications.likesByGameId)
          .flat()
          .filter(like => like.status === 'unread').length : 0
    };
  };

  // Group comments by gameId for display
  const getCommentsGroupedByGame = () => {
    return newComments.reduce((groups, comment) => {
      const gameId = comment.gameId;
      if (!groups[gameId]) {
        groups[gameId] = [];
      }
      groups[gameId].push(comment);
      return groups;
    }, {});
  };

  // Fetch notifications when user handle changes
  useEffect(() => {
    if (authenticatedUserHandle) {
      fetchNotifications();
    }
  }, [authenticatedUserHandle]);

  return {
    // Data
    newFollowers,
    newComments,
    notifications,
    loading,
    error,
    
    // Helper functions
    hasNewFollowers,
    hasNewComments,
    hasNewLikes,
    getNotificationCounts,
    getCommentsGroupedByGame,
    checkGameExists,
    
    // Refetch function
    refetchNotifications: fetchNotifications,
    
    // State setters for external updates
    setNewFollowers,
    setNewComments,
    setNotifications
  };
};